import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../Components/Modal/Modal';
import { Plus, Trash, Search, ChevronDown, ChevronUp, FileText, BarChart3, Leaf, ClipboardList, Scissors, Settings2, AlertTriangle, CheckCircle2, Recycle, RefreshCw, Wifi, WifiOff, LogOut } from 'lucide-react';
import { api, setCurrentPageContext } from '../../services/api';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { ResponsibleQueryModule } from '../../Components/warehouse/ResponsibleQueryModule';
import BasePodasAmbientalModule from './BasePodasAmbientalModule';
import GestionResiduosAmbientalModule from './GestionResiduosAmbientalModule';
import ControlServiciosAmbientalModule from './ControlServiciosAmbientalModule';
import EspeciesAmbientalModule from './EspeciesAmbientalModule';
import InspeccionesAmbientalModule, { INSPECCIONES_AMBIENTALES_OPTIONS } from './InspeccionesAmbientalModule';
import DocumentacionFormulariosModule, { DOCUMENTACION_FORMULARIOS_OPTIONS } from './DocumentacionFormulariosModule';
import InspeccionesSSTFormulariosModule, { INSPECCIONES_SST_OPTIONS } from './InspeccionesSSTFormulariosModule';
import PersonalTrackingModule from './PersonalTrackingModule';
import IndicadoresAmbientalesModule from './IndicadoresAmbientalesModule';
import { isNativeRuntime } from '../../utils/runtimePlatform';

export default function SGIPanel({
  location,
  showLocation = true,
  people = [],
  materials = [],
  assignments = [],
  warehouses = [],
  sigMobileMode = false,
}) {
  const navigate = useNavigate();
  const { currentUser, logout } = useUserPrefs();
  const isSigMobile = !!sigMobileMode || String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';
  const viewerRole = String(currentUser?.role || '').trim();
  const isProd = process.env.NODE_ENV === 'production';
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
  };
  const title = displayNames[location] || location;

  // Helpers de fecha
  const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Normalizador de cédulas: solo dígitos y sin ceros a la izquierda
  const normalizeId = useCallback((v) => String(v || '').replace(/[^0-9]/g, ''), []);
  const canonicalId = useCallback((v) => normalizeId(v).replace(/^0+/, ''), [normalizeId]);

  // Estado para Solicitud Pedido
  // eslint-disable-next-line no-unused-vars
  const STORAGE_KEY = 'sgi_solicitudes_pedido';
  const [isPedidoOpen, setIsPedidoOpen] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [isDotacionesOpen, setIsDotacionesOpen] = useState(false);
  const [accordion, setAccordion] = useState(isSigMobile ? '' : 'formularios');
  // Reporta a Administración TI > Auditoría qué submódulo de SGI está
  // viendo el usuario (ver X-Senerfi-Page en src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/sig/${accordion || ''}`);
  }, [location, accordion]);
  const [ambientalTab, setAmbientalTab] = useState('inspecciones');
  const [inspeccionesAmbientalTab, setInspeccionesAmbientalTab] = useState('sustancias_quimicas');
  const [basePodasTab, setBasePodasTab] = useState('ceo');
  const [formulariosTab, setFormulariosTab] = useState(isSigMobile ? 'inspecciones' : 'accidentalidad');
  const [inspeccionesSstTab, setInspeccionesSstTab] = useState('equipos_alturas');
  const [documentacionTab, setDocumentacionTab] = useState('checklist_equipos_alturas');
  const [reportesTab, setReportesTab] = useState('cumplimiento');
  const [indicadoresTab, setIndicadoresTab] = useState('no_conformidades');
  const [pendingSyncCount, setPendingSyncCount] = useState(() => (isSigMobile ? api.sync.getPendingCount() : 0));
  const [isOfflineMode, setIsOfflineMode] = useState(() => (isSigMobile ? api.sync.isOffline() : false));
  const [isSyncing, setIsSyncing] = useState(false);
  const [autoSyncNote, setAutoSyncNote] = useState('');
  const syncingRef = useRef(false);

  const refreshSyncState = useCallback(() => {
    if (!isSigMobile) return;
    setPendingSyncCount(api.sync.getPendingCount());
    setIsOfflineMode(api.sync.isOffline());
  }, [isSigMobile]);

  // Sincronización silenciosa (sin alert) compartida entre el botón manual y
  // el disparo automático al recuperar conexión.
  const runSync = useCallback(async () => {
    if (syncingRef.current) return null;
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      const res = await api.sync.flushPending();
      refreshSyncState();
      try { window.dispatchEvent(new Event('senerfi:sig-mobile-sync')); } catch (_) {}
      return res;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [refreshSyncState]);

  useEffect(() => {
    if (!isSigMobile) return undefined;
    refreshSyncState();
    const syncHandler = () => refreshSyncState();
    const onlineHandler = async () => {
      refreshSyncState();
      if (api.sync.getPendingCount() === 0) return;
      try {
        const res = await runSync();
        setAutoSyncNote(res?.message || 'Sincronización automática completada.');
      } catch (err) {
        setAutoSyncNote(err?.message || 'No se pudo sincronizar automáticamente.');
      }
    };
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', syncHandler);
    window.addEventListener('senerfi:sig-mobile-sync', syncHandler);
    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', syncHandler);
      window.removeEventListener('senerfi:sig-mobile-sync', syncHandler);
    };
  }, [isSigMobile, refreshSyncState, runSync]);

  const handleSyncNow = useCallback(async () => {
    if (!isSigMobile) return;
    try {
      const res = await runSync();
      if (res) {
        setAutoSyncNote('');
        alert(res?.message || 'Sincronización finalizada.');
      }
    } catch (err) {
      alert(err?.message || 'No fue posible sincronizar los datos.');
    }
  }, [isSigMobile, runSync]);

  const handleSigMobileLogout = useCallback(() => {
    try { logout(); } catch (_) {}
    try { if (typeof window !== 'undefined') { sessionStorage.setItem('senerfi_show_logout', '1'); } } catch (_) {}
    try { if (typeof window !== 'undefined') { localStorage.removeItem('senerfi_user_prefs'); } } catch (_) {}
    try {
      if (isNativeRuntime()) {
        const base = window.location.href.split('#')[0];
        const target = `${base}#/`;
        try { window.location.replace(target); } catch (_) {}
      } else {
        navigate('/', { replace: true });
      }
      setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 120);
    } catch (_) {}
  }, [logout, navigate]);

  const mobileTopLevelGroupClass = isSigMobile
    ? 'grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-white/85 p-2 shadow-sm'
    : 'flex gap-2 flex-wrap';
  const mobileSubLevelGroupClass = isSigMobile
    ? 'rounded-2xl border border-slate-200 bg-slate-50/90 p-3 shadow-inner'
    : '';

  const allowedDocumentationOptions = useMemo(() => {
    const role = String(viewerRole || '').trim();
    return (DOCUMENTACION_FORMULARIOS_OPTIONS || []).filter((opt) => {
      const allowed = Array.isArray(opt?.allowedRoles) ? opt.allowedRoles : null;
      if (!allowed || allowed.length === 0) return true;
      return allowed.includes(role);
    });
  }, [viewerRole]);

  useEffect(() => {
    if (formulariosTab === 'documentacion') {
      if (allowedDocumentationOptions.length === 0) {
        setFormulariosTab('inspecciones');
        return;
      }
      const exists = allowedDocumentationOptions.some((o) => o.key === documentacionTab);
      if (!exists) setDocumentacionTab(allowedDocumentationOptions[0].key);
    }
  }, [allowedDocumentationOptions, documentacionTab, formulariosTab]);


  const emptyItem = { description: '', quantity: 1, unit: 'Unidad', materialId: null };
  const [form, setForm] = useState({
    requesterId: '',
    requesterName: '',
    collaboratorId: '',
    collaboratorName: '',
    project: '',
    date: toYMD(new Date()),
    items: [{ ...emptyItem }],
    notes: '',
  });

  // Fuente de personas: usa prop y si está vacía, intenta cargar desde localStorage
  const [peopleSource, setPeopleSource] = useState(people || []);
  useEffect(() => {
    setPeopleSource(people || []);
  }, [isProd, people]);
  useEffect(() => {
    if (!isProd && (people || []).length === 0) {
      try {
        const raw = localStorage.getItem('senerfi_people');
        const list = raw ? JSON.parse(raw) : [];
        if (Array.isArray(list) && list.length > 0) setPeopleSource(list);
      } catch {}
    }
  }, [isProd, people]);

  // Fuente de materiales: usa prop y si está vacía, intenta cargar desde localStorage
  const [materialsSource, setMaterialsSource] = useState(materials || []);
  useEffect(() => {
    setMaterialsSource(materials || []);
  }, [isProd, materials]);
  useEffect(() => {
    if (!isProd && (materials || []).length === 0) {
      try {
        const raw = localStorage.getItem('senerfi_materials');
        const list = raw ? JSON.parse(raw) : [];
        if (Array.isArray(list) && list.length > 0) setMaterialsSource(list);
      } catch {}
    }
  }, [isProd, materials]);

  // Colaboradores activos (para consultar por cédula)
  const activePeople = useMemo(
    () => (peopleSource || []).filter((p) => String(p.status || '').trim().toLowerCase().includes('activo')),
    [peopleSource]
  );

  // Búsqueda mixta de persona por cédula o nombre
  const findPersonByInput = useCallback((raw) => {
    const str = String(raw || '').trim();
    if (!str) return null;
    const byId = activePeople.find((p) => canonicalId(p.id) === canonicalId(str));
    if (byId) return byId;
    const lower = str.toLowerCase();
    const exactByName = activePeople.find((p) => String(p.name || '').trim().toLowerCase() === lower);
    if (exactByName) return exactByName;
    const candidates = activePeople.filter((p) => String(p.name || '').toLowerCase().includes(lower));
    return candidates.length === 1 ? candidates[0] : null;
  }, [activePeople, canonicalId]);

  // Candidatos por input (para sugerencias y fallback en guardar)
  const getCandidatesByInput = useCallback((raw) => {
    const q = String(raw || '').trim();
    if (!q) return activePeople;
    const qId = canonicalId(q);
    const qLower = q.toLowerCase();
    return activePeople.filter(
      (p) => canonicalId(p.id).startsWith(qId) || String(p.name || '').toLowerCase().includes(qLower)
    );
  }, [activePeople, canonicalId]);

  // Selección de materiales con stock disponible
  const warehouseId = useMemo(() => {
    if (location === 'santander-de-quilichao') return 'santander';
    return 'popayan';
  }, [location]);
  const [stockWarehouseId, setStockWarehouseId] = useState(warehouseId);
  useEffect(() => { setStockWarehouseId(warehouseId); }, [warehouseId]);
  const [warehouseOptions, setWarehouseOptions] = useState([{ id: 'popayan', name: 'Popayán' }, { id: 'santander', name: 'Santander de Quilichao' }]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.warehouses.list({ limit: 1000 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const apiOpts = rows.map((w) => ({ id: String(w.id).toLowerCase(), name: w.name || String(w.id) }));
        const fromMaterials = Array.isArray(materialsSource)
          ? Array.from(new Set((materialsSource || []).flatMap((m) => Object.keys(m.stockByWarehouse || {})))).map((id) => ({ id: String(id).toLowerCase(), name: id }))
          : [];
        const union = [...apiOpts, ...fromMaterials];
        const seen = new Set();
        const unique = union.filter((w) => { const k = w.id; if (seen.has(k)) return false; seen.add(k); return true; });
        const final = unique.length
          ? unique.sort((a, b) => String(a.name).localeCompare(String(b.name)))
          : [{ id: 'popayan', name: 'Popayán' }, { id: 'santander', name: 'Santander de Quilichao' }];
        setWarehouseOptions([{ id: 'todas', name: 'Todas las bodegas' }, ...final]);
      } catch {
        setWarehouseOptions([{ id: 'todas', name: 'Todas las bodegas' }, { id: 'popayan', name: 'Popayán' }, { id: 'santander', name: 'Santander de Quilichao' }]);
      }
    })();
  }, [materialsSource]);

  const [categoryFilter, setCategoryFilter] = useState('Todas');
  const norm = useCallback((s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim(), []);
  const categoryOptions = useMemo(() => {
    const set = new Set();
    (materialsSource || []).forEach((m) => {
      const c = String(m.category || m.type || '').trim();
      if (c) set.add(c);
    });
    const list = Array.from(set).sort((a, b) => a.localeCompare(b));
    return ['Todas', ...list];
  }, [materialsSource]);

  const [showStockPanel, setShowStockPanel] = useState(false);
  const [stockQuery, setStockQuery] = useState('');

  const availableMaterials = useMemo(() => {
    const list = Array.isArray(materialsSource) ? materialsSource : [];
    const byWh = (m) => {
      const sbw = m?.stockByWarehouse || {};
      if (stockWarehouseId === 'todas') {
        const total = Object.values(sbw).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
        return total > 0;
      }
      return parseInt(sbw[stockWarehouseId] || 0) > 0;
    };
    const byCat = (m) => {
      if (!categoryFilter || categoryFilter === 'Todas') return true;
      const c = norm(m.category || m.type || '');
      const f = norm(categoryFilter);
      return c.includes(f);
    };
    return list.filter((m) => byWh(m) && byCat(m));
  }, [materialsSource, stockWarehouseId, categoryFilter, norm]);

  const stockSuggestions = useMemo(() => {
    const q = stockQuery.trim().toLowerCase();
    let list = availableMaterials;
    if (q) {
      list = list.filter((m) => [m.code, m.name, m.brand, m.category]
        .some((v) => String(v ?? '').toLowerCase().includes(q)));
    }
    return list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [stockQuery, availableMaterials]);

  const addItemFromMaterial = (m) => {
    const desc = `${m.name}${m.brand ? ' - ' + m.brand : ''}`;
    const unit = m.unit || 'Unidad';
    setForm((f) => ({
      ...f,
      items: [...f.items, { description: desc, quantity: 1, unit, materialId: m.id }],
    }));
  };

  const [showCollaboratorSug, setShowCollaboratorSug] = useState(false);
  const collaboratorSuggestions = useMemo(() => {
    const list = getCandidatesByInput(form.collaboratorId);
    return list.slice(0, 8);
  }, [form.collaboratorId, getCandidatesByInput]);

  useEffect(() => {
    const raw = String(form.collaboratorId || '').trim();
    const idLen = canonicalId(raw).length;
    const textLen = raw.length;
    if (!form.collaboratorName && (idLen >= 4 || textLen >= 3)) {
      const all = getCandidatesByInput(raw);
      if (all.length === 1) {
        const p = all[0];
        setForm((f) => ({ ...f, collaboratorId: p.id, collaboratorName: p.name }));
      }
    }
  }, [form.collaboratorId, form.collaboratorName, getCandidatesByInput, canonicalId]);

  const pedidosPorSede = useMemo(
    () => (pedidos || []).filter((p) => p.location === location).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [pedidos, location]
  );


  // eslint-disable-next-line no-unused-vars
  const persistPedidos = (list) => {
    setPedidos(list);
  };

  const openNuevoPedido = () => {
    const requesterId = String(currentUser?.responsibleCedula || currentUser?.responsible_cedula || currentUser?.id || '').trim();
    const requesterName = String(currentUser?.name || '').trim();
    setForm({
      requesterId,
      requesterName,
      collaboratorId: '',
      collaboratorName: '',
      project: '',
      date: toYMD(new Date()),
      items: [{ ...emptyItem }],
      notes: '',
    });
    setIsPedidoOpen(true);
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));


  useEffect(() => {
    (async () => {
      try {
        const res = await api.sgi.requests.list({ location, limit: 500, order: 'desc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map((r) => ({
          id: r.id,
          requesterId: r.requester_id ?? r.requesterId ?? '',
          requesterName: r.requester_name ?? r.requesterName ?? '',
          project: r.project ?? '',
          date: r.date ?? (r.created_at ? String(r.created_at).slice(0, 10) : ''),
          items: Array.isArray(r.items) ? r.items : [],
          notes: r.notes ?? '',
          status: r.status ?? 'Pendiente',
          location: r.location ?? location,
          createdAt: r.created_at ?? new Date().toISOString(),
        }));
        if (mapped.length) setPedidos(mapped);
      } catch (err) {
        console.warn('No se pudo cargar solicitudes desde el servidor', err);
      }
    })();
  }, [location]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = (form.items || []).filter((it) => (it.description || '').trim() && Number(it.quantity) > 0);
    const requesterDoc = String(currentUser?.responsibleCedula || currentUser?.responsible_cedula || form.requesterId || '').trim();
    let person = requesterDoc ? findPersonByInput(requesterDoc) : null;
    if (!person && requesterDoc) {
      const cands = getCandidatesByInput(requesterDoc);
      if (cands.length === 1) person = cands[0];
    }
    if (!form.date || validItems.length === 0) {
      alert('Completa fecha y al menos un ítem.');
      return;
    }
    const requesterIdFinal = String(person?.id || requesterDoc || currentUser?.id || form.requesterId || '').trim();
    const requesterNameFinal = String(person?.name || currentUser?.name || form.requesterName || requesterIdFinal).trim();
    const payload = {
      requester_id: requesterIdFinal,
      requester_name: requesterNameFinal,
      requester_email: String(currentUser?.email || '').trim() || null,
      collaborator_id: form.collaboratorId || null,
      collaborator_name: form.collaboratorName || null,
      project: form.project || '',
      date: form.date,
      location,
      items: validItems,
      notes: form.notes || '',
      status: 'Pendiente',
    };
    try {
      const res = await api.sgi.requests.upsert(payload);
      const newId = res?.id ?? res?.data?.id ?? Date.now();
      const code = res?.code || `PED-${String(newId).padStart(6, '0')}`;
      const record = { id: newId, ...payload, createdAt: new Date().toISOString() };
      setPedidos((prev) => [record, ...prev]);
      console.log('[SGI] Solicitud creada', { id: newId, requester: payload.requester_name, location: payload.location, items: payload.items.length });
      const emailSent = res?.email_sent;
      if (emailSent === false) {
        alert(`Pedido creado: ${code}\nNo se pudo enviar el correo al solicitante.`);
      } else {
        alert(`Pedido creado: ${code}`);
      }
    } catch (err) {
      console.error('[SGI] Error al guardar solicitud', err);
      alert('No se pudo guardar en el servidor. Verifica conexión con XAMPP.');
      return;
    }
    setIsPedidoOpen(false);
    try {
      localStorage.setItem('senerfi_ui_nav_next', JSON.stringify({ module: 'almacen', almacenGroup: 'movimientos', almacenSection: 'pedidos', location }));
    } catch (_) {}
    try { window.dispatchEvent(new Event('senerfi:nav_next')); } catch (_) {}
  };

  const deletePedido = async (id) => {
    try { 
      await api.sgi.requests.delete({ id });
      console.log('[SGI] Solicitud eliminada', { id });
    } catch (err) {
      console.error('[SGI] Error al eliminar solicitud', err);
    }
    setPedidos((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div className={`text-gray-800 ${isSigMobile ? 'max-w-md mx-auto' : ''}`}>
      {isSigMobile ? (
        <div
          className="mb-5 relative overflow-hidden rounded-[30px] border px-5 py-6 text-white"
          style={{
            borderColor: '#166534',
            background: 'linear-gradient(135deg, #064e3b 0%, #166534 55%, #14532d 100%)',
            boxShadow: '0 26px 60px rgba(6, 78, 59, 0.30)',
          }}
        >
          <button
            type="button"
            onClick={handleSigMobileLogout}
            className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full border shadow-sm"
            style={{ borderColor: 'rgba(254,202,202,0.55)', backgroundColor: 'rgba(220,38,38,0.92)', backdropFilter: 'blur(8px)' }}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <LogOut size={16} className="text-white" strokeWidth={2.4} />
          </button>
          <div
            className="absolute inset-y-0 left-0 w-2"
            style={{ background: 'linear-gradient(180deg, #22c55e 0%, #86efac 100%)' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'radial-gradient(circle at top right, rgba(74, 222, 128, 0.22), transparent 24%), radial-gradient(circle at bottom left, rgba(187, 247, 208, 0.16), transparent 28%)' }}
          />
          <div className="absolute -top-12 right-[-18px] h-28 w-28 rounded-full blur-3xl" style={{ background: 'rgba(74, 222, 128, 0.18)' }} />
          <div className="absolute -bottom-12 left-10 h-24 w-24 rounded-full blur-3xl" style={{ background: 'rgba(187, 247, 208, 0.12)' }} />
          <div className="relative mt-2 flex flex-col items-center">
            <div className="mb-3 h-1 w-20 rounded-full" style={{ background: 'linear-gradient(90deg, #22c55e 0%, #bbf7d0 100%)' }} />
            <h2 className="text-center text-[34px] font-extrabold leading-tight text-white">
              Sistema Integrado
              <span className="block text-emerald-200">de Gestion</span>
            </h2>
          </div>
          <p className="relative mt-2 text-center text-xs leading-5 text-slate-200">Formularios y gestion ambiental disponibles en modo movil.</p>
          <div className="relative mt-5 flex items-center justify-center gap-2 flex-wrap">
            <div
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border shadow-sm font-semibold ${isOfflineMode ? 'text-yellow-900 border-yellow-200' : 'text-white border-emerald-300'}`}
              style={{ backgroundColor: isOfflineMode ? 'rgba(254,243,199,0.96)' : 'rgba(22,163,74,0.95)', backdropFilter: 'blur(8px)' }}
            >
              {isOfflineMode ? <WifiOff size={14} /> : <Wifi size={14} />}
              {isOfflineMode ? 'Modo offline activo' : 'Conectado'}
            </div>
            <div
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border text-white shadow-sm font-semibold"
              style={{ borderColor: 'rgba(254, 202, 202, 0.45)', backgroundColor: 'rgba(220, 38, 38, 0.92)', backdropFilter: 'blur(8px)' }}
            >
              Pendientes: {pendingSyncCount}
            </div>
          </div>
        </div>
      ) : (
        <h2 className="text-2xl font-semibold mb-2">SIG</h2>
      )}
      {showLocation && !isSigMobile && (
        <p className="text-gray-700">Sede: {title}</p>
      )}

      <div className="mt-6 space-y-3">
        <div className={`bg-white ${isSigMobile ? 'rounded-[26px] shadow-[0_18px_45px_rgba(15,23,42,0.10)] border-slate-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50' : 'rounded-xl shadow'} border overflow-hidden ${accordion === 'formularios' ? 'border-blue-200 ring-1 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}>
          <button
            type="button"
            onClick={() => setAccordion((v) => (v === 'formularios' ? '' : 'formularios'))}
            className={`w-full flex items-center justify-between relative ${isSigMobile ? 'px-5 py-5' : 'px-4 py-4'}`}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: 'linear-gradient(180deg, #3B82F6 0%, #4F46E5 100%)' }} />
            <div className="flex items-center gap-3 text-left">
              <div className={`${isSigMobile ? 'w-12 h-12 rounded-2xl' : 'w-10 h-10 rounded-xl'} flex items-center justify-center text-white shadow-sm`} style={{ background: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)' }}>
                <FileText size={18} className="text-white" />
              </div>
              <div>
                <div className={`${isSigMobile ? 'text-lg' : ''} font-semibold`}>Formularios</div>
                <div className={`text-gray-600 ${isSigMobile ? 'text-[13px]' : 'text-sm'}`}>Solicitudes y registros internos</div>
              </div>
            </div>
            {accordion === 'formularios' ? <ChevronUp className="text-blue-600" size={18} /> : <ChevronDown className="text-gray-500" size={18} />}
          </button>
          {accordion === 'formularios' && (
            <div className="border-t px-4 pb-5">
              <div className="pt-4">
                {isSigMobile && (
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Area principal</div>
                )}
                <div className={mobileTopLevelGroupClass}>
                  {!isSigMobile && (
                    <button
                      type="button"
                      onClick={() => setFormulariosTab('pedido')}
                      className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${formulariosTab === 'pedido' ? 'text-white border-blue-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-blue-700'}`}
                      style={formulariosTab === 'pedido' ? { background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' } : undefined}
                    >
                      <Plus size={14} className={formulariosTab === 'pedido' ? 'text-white' : 'text-blue-600'} /> Solicitud Pedido
                    </button>
                  )}
                  {!isSigMobile && (
                    <button
                      type="button"
                      onClick={() => setFormulariosTab('accidentalidad')}
                      className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${formulariosTab === 'accidentalidad' ? 'text-white border-yellow-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-yellow-700'}`}
                      style={formulariosTab === 'accidentalidad' ? { background: 'linear-gradient(90deg, #D97706 0%, #F97316 100%)' } : undefined}
                    >
                      <AlertTriangle size={14} className={formulariosTab === 'accidentalidad' ? 'text-white' : 'text-yellow-600'} /> Accidentalidad
                    </button>
                  )}
                  {!isSigMobile && (
                    <button
                      type="button"
                      onClick={() => setFormulariosTab('ausentismos')}
                      className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${formulariosTab === 'ausentismos' ? 'text-white border-orange-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-orange-700'}`}
                      style={formulariosTab === 'ausentismos' ? { background: 'linear-gradient(90deg, #EA580C 0%, #EF4444 100%)' } : undefined}
                    >
                      <CheckCircle2 size={14} className={formulariosTab === 'ausentismos' ? 'text-white' : 'text-orange-600'} /> Ausentismos
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setFormulariosTab('inspecciones')}
                    className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${formulariosTab === 'inspecciones' ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                    style={formulariosTab === 'inspecciones' ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                  >
                    <ClipboardList size={14} className={formulariosTab === 'inspecciones' ? 'text-white' : 'text-emerald-600'} /> Inspecciones
                  </button>
                  {allowedDocumentationOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormulariosTab('documentacion')}
                      className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${formulariosTab === 'documentacion' ? 'text-white border-violet-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-violet-700'}`}
                      style={formulariosTab === 'documentacion' ? { background: 'linear-gradient(90deg, #7C3AED 0%, #4F46E5 100%)' } : undefined}
                    >
                      <FileText size={14} className={formulariosTab === 'documentacion' ? 'text-white' : 'text-violet-600'} /> Documentación
                    </button>
                  )}
                </div>

                {formulariosTab === 'documentacion' && (
                  <div className="mt-4 space-y-4">
                    <div className={mobileSubLevelGroupClass}>
                      {isSigMobile && (
                        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tipo de documento</div>
                      )}
                      <div className={`flex flex-wrap gap-2 ${isSigMobile ? 'justify-start' : ''}`}>
                        {allowedDocumentationOptions.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => setDocumentacionTab(option.key)}
                            className={`px-3 ${isSigMobile ? 'py-2 min-h-[40px]' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${documentacionTab === option.key ? 'text-white border-violet-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-violet-700'}`}
                            style={documentacionTab === option.key ? { background: 'linear-gradient(90deg, #7C3AED 0%, #4F46E5 100%)' } : undefined}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <DocumentacionFormulariosModule location={location} documentationType={documentacionTab} />
                  </div>
                )}

                {formulariosTab === 'inspecciones' && (
                  <div className="mt-4 space-y-4">
                    <div className={mobileSubLevelGroupClass}>
                      {isSigMobile && (
                        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tipo de inspeccion</div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {INSPECCIONES_SST_OPTIONS.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => setInspeccionesSstTab(option.key)}
                            className={`px-3 ${isSigMobile ? 'py-2 min-h-[40px]' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${inspeccionesSstTab === option.key ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                            style={inspeccionesSstTab === option.key ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <InspeccionesSSTFormulariosModule location={location} inspectionType={inspeccionesSstTab} />
                  </div>
                )}

                {formulariosTab === 'accidentalidad' && (
                  <div className="mt-4">
                    <PersonalTrackingModule
                      location={location}
                      people={activePeople}
                      reportKind="accidentalidad"
                    />
                  </div>
                )}

                {formulariosTab === 'ausentismos' && (
                  <div className="mt-4">
                    <PersonalTrackingModule
                      location={location}
                      people={activePeople}
                      reportKind="ausentismo"
                    />
                  </div>
                )}

                {formulariosTab === 'pedido' && (
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={openNuevoPedido}
                        className="px-3 py-1.5 text-xs rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white border border-blue-700 hover:brightness-110 flex items-center gap-1 shadow-sm"
                      >
                        <Plus size={14} /> Nueva solicitud
                      </button>
                    </div>

                    {pedidosPorSede.length > 0 ? (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-800">Solicitudes recientes</h4>
                          <span className="text-xs text-gray-500">Mostrando {Math.min(5, pedidosPorSede.length)} de {pedidosPorSede.length}</span>
                        </div>
                        <div className="overflow-x-auto border rounded">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                              <tr>
                                <th className="px-3 py-2 text-left">Fecha</th>
                                <th className="px-3 py-2 text-left">Solicitante</th>
                                <th className="px-3 py-2 text-left">Proyecto</th>
                                <th className="px-3 py-2 text-left">Ítems</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {pedidosPorSede.slice(0, 5).map((r) => (
                                <tr key={r.id} className="border-t">
                                  <td className="px-3 py-2">{r.date}</td>
                                  <td className="px-3 py-2">{(r.requesterName || r.requester) ? `${r.requesterName || r.requester} ${r.requesterId ? `(${r.requesterId})` : ''}` : (r.requesterId || '-')}</td>
                                  <td className="px-3 py-2">{r.project || '-'}</td>
                                  <td className="px-3 py-2">{(r.items || []).length}</td>
                                  <td className="px-3 py-2">{r.status}</td>
                                  <td className="px-3 py-2 text-right">
                                    <button type="button" onClick={() => deletePedido(r.id)} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700">
                                      <Trash size={14} />
                                      Eliminar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No hay solicitudes recientes para esta sede.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isSigMobile && (
          <div className={`bg-white rounded-xl shadow border overflow-hidden ${accordion === 'reportes' ? 'border-emerald-200 ring-1 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'}`}>
            <button
              type="button"
              onClick={() => setAccordion((v) => (v === 'reportes' ? '' : 'reportes'))}
              className="w-full px-4 py-4 flex items-center justify-between relative"
            >
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: 'linear-gradient(180deg, #10B981 0%, #0D9488 100%)' }} />
              <div className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}>
                  <ClipboardList size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold">Reportes</div>
                  <div className="text-sm text-gray-600">Visualización y descarga de informes</div>
                </div>
              </div>
              {accordion === 'reportes' ? <ChevronUp className="text-emerald-600" size={18} /> : <ChevronDown className="text-gray-500" size={18} />}
            </button>
            {accordion === 'reportes' && (
              <div className="border-t px-4 pb-5">
                <div className="pt-4 flex gap-2 flex-wrap items-center">
                  <button
                    type="button"
                    onClick={() => setReportesTab('cumplimiento')}
                    className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${reportesTab === 'cumplimiento' ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                    style={reportesTab === 'cumplimiento' ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                  >
                    <BarChart3 size={14} className={reportesTab === 'cumplimiento' ? 'text-white' : 'text-emerald-600'} /> Cumplimiento
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDotacionesOpen(true)}
                    className="px-3 py-1.5 text-xs rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white border border-blue-700 hover:brightness-110 shadow-sm"
                  >
                    Imprimir dotaciones
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportesTab('incidentes')}
                    className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${reportesTab === 'incidentes' ? 'text-white border-orange-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-orange-700'}`}
                    style={reportesTab === 'incidentes' ? { background: 'linear-gradient(90deg, #EA580C 0%, #F59E0B 100%)' } : undefined}
                  >
                    <AlertTriangle size={14} className={reportesTab === 'incidentes' ? 'text-white' : 'text-orange-600'} /> Incidentes
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportesTab('capacitaciones')}
                    className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${reportesTab === 'capacitaciones' ? 'text-white border-blue-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-blue-700'}`}
                    style={reportesTab === 'capacitaciones' ? { background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' } : undefined}
                  >
                    <FileText size={14} className={reportesTab === 'capacitaciones' ? 'text-white' : 'text-blue-600'} /> Capacitaciones
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!isSigMobile && (
        <div className={`bg-white rounded-xl shadow border overflow-hidden ${accordion === 'indicadores' ? 'border-orange-200 ring-1 ring-orange-100' : 'border-gray-200 hover:border-gray-300'}`}>
          <button
            type="button"
            onClick={() => setAccordion((v) => (v === 'indicadores' ? '' : 'indicadores'))}
            className="w-full px-4 py-4 flex items-center justify-between relative"
          >
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: 'linear-gradient(180deg, #F97316 0%, #D97706 100%)' }} />
            <div className="flex items-center gap-3 text-left">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #D97706 100%)' }}>
                <BarChart3 size={18} className="text-white" />
              </div>
              <div>
                <div className="font-semibold">Indicadores</div>
                <div className="text-sm text-gray-600">Tableros y métricas clave</div>
              </div>
            </div>
            {accordion === 'indicadores' ? <ChevronUp className="text-orange-600" size={18} /> : <ChevronDown className="text-gray-500" size={18} />}
          </button>
          {accordion === 'indicadores' && (
            <div className="border-t px-4 pb-5">
              <div className="pt-4 flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setIndicadoresTab('no_conformidades')}
                  className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${indicadoresTab === 'no_conformidades' ? 'text-white border-orange-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-orange-700'}`}
                  style={indicadoresTab === 'no_conformidades' ? { background: 'linear-gradient(90deg, #EA580C 0%, #F59E0B 100%)' } : undefined}
                >
                  <AlertTriangle size={14} className={indicadoresTab === 'no_conformidades' ? 'text-white' : 'text-orange-600'} /> No conformidades
                </button>
                <button
                  type="button"
                  onClick={() => setIndicadoresTab('acciones_cerradas')}
                  className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${indicadoresTab === 'acciones_cerradas' ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                  style={indicadoresTab === 'acciones_cerradas' ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                >
                  <CheckCircle2 size={14} className={indicadoresTab === 'acciones_cerradas' ? 'text-white' : 'text-emerald-600'} /> Acciones cerradas
                </button>
                <button
                  type="button"
                  onClick={() => setIndicadoresTab('frecuencia_incidentes')}
                  className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${indicadoresTab === 'frecuencia_incidentes' ? 'text-white border-blue-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-blue-700'}`}
                  style={indicadoresTab === 'frecuencia_incidentes' ? { background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' } : undefined}
                >
                  <BarChart3 size={14} className={indicadoresTab === 'frecuencia_incidentes' ? 'text-white' : 'text-blue-600'} /> Frecuencia incidentes
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        <div className={`bg-white ${isSigMobile ? 'rounded-[26px] shadow-[0_18px_45px_rgba(15,23,42,0.10)] border-slate-200 bg-gradient-to-r from-lime-50 via-white to-emerald-50' : 'rounded-xl shadow'} border overflow-hidden ${accordion === 'ambiental' ? 'border-lime-200 ring-1 ring-lime-100' : 'border-gray-200 hover:border-gray-300'}`}>
          <button
            type="button"
            onClick={() => setAccordion((v) => (v === 'ambiental' ? '' : 'ambiental'))}
            className={`w-full flex items-center justify-between relative ${isSigMobile ? 'px-5 py-5' : 'px-4 py-4'}`}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: 'linear-gradient(180deg, #84CC16 0%, #16A34A 100%)' }} />
            <div className="flex items-center gap-3 text-left">
              <div className={`${isSigMobile ? 'w-12 h-12 rounded-2xl' : 'w-10 h-10 rounded-xl'} flex items-center justify-center text-white shadow-sm`} style={{ background: 'linear-gradient(135deg, #65A30D 0%, #16A34A 100%)' }}>
                <Leaf size={18} className="text-white" />
              </div>
              <div>
                <div className={`${isSigMobile ? 'text-lg' : ''} font-semibold`}>Ambiental</div>
                <div className={`text-gray-600 ${isSigMobile ? 'text-[13px]' : 'text-sm'}`}>Gestión y seguimiento ambiental</div>
              </div>
            </div>
            {accordion === 'ambiental' ? <ChevronUp className="text-lime-600" size={18} /> : <ChevronDown className="text-gray-500" size={18} />}
          </button>
          {accordion === 'ambiental' && (
            <div className="border-t px-4 pb-5">
              <div className="pt-4">
                {isSigMobile && (
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Servicios ambientales</div>
                )}
                <div className={mobileTopLevelGroupClass}>
                <button
                  type="button"
                  onClick={() => setAmbientalTab('inspecciones')}
                  className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${ambientalTab === 'inspecciones' ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                  style={ambientalTab === 'inspecciones' ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                >
                  <ClipboardList size={14} className={ambientalTab === 'inspecciones' ? 'text-white' : 'text-emerald-600'} /> Inspecciones
                </button>
                <button
                  type="button"
                  onClick={() => setAmbientalTab('base_podas')}
                  className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${ambientalTab === 'base_podas' ? 'text-white border-lime-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-lime-700'}`}
                  style={ambientalTab === 'base_podas' ? { background: 'linear-gradient(90deg, #65A30D 0%, #16A34A 100%)' } : undefined}
                >
                  <Scissors size={14} className={ambientalTab === 'base_podas' ? 'text-white' : 'text-lime-600'} /> Base podas
                </button>
                <button
                  type="button"
                  onClick={() => setAmbientalTab('gestion_residuos')}
                  className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${ambientalTab === 'gestion_residuos' ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                  style={ambientalTab === 'gestion_residuos' ? { background: 'linear-gradient(90deg, #047857 0%, #059669 100%)' } : undefined}
                >
                  <Recycle size={14} className={ambientalTab === 'gestion_residuos' ? 'text-white' : 'text-emerald-700'} /> Gestión de residuos
                </button>
                <button
                  type="button"
                  onClick={() => setAmbientalTab('control_servicios')}
                  className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${ambientalTab === 'control_servicios' ? 'text-white border-blue-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-blue-700'}`}
                  style={ambientalTab === 'control_servicios' ? { background: 'linear-gradient(90deg, #2563EB 0%, #4F46E5 100%)' } : undefined}
                >
                  <Settings2 size={14} className={ambientalTab === 'control_servicios' ? 'text-white' : 'text-blue-600'} /> Control servicios
                </button>
                <button
                  type="button"
                  onClick={() => setAmbientalTab('especies')}
                  className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${ambientalTab === 'especies' ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                  style={ambientalTab === 'especies' ? { background: 'linear-gradient(90deg, #16A34A 0%, #15803D 100%)' } : undefined}
                >
                  <Leaf size={14} className={ambientalTab === 'especies' ? 'text-white' : 'text-emerald-600'} /> Especies
                </button>
                {!isSigMobile && (
                  <button
                    type="button"
                    onClick={() => setAmbientalTab('indicadores')}
                    className={`px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${ambientalTab === 'indicadores' ? 'text-white border-orange-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-orange-700'}`}
                    style={ambientalTab === 'indicadores' ? { background: 'linear-gradient(90deg, #EA580C 0%, #F59E0B 100%)' } : undefined}
                  >
                    <BarChart3 size={14} className={ambientalTab === 'indicadores' ? 'text-white' : 'text-orange-600'} /> Indicadores
                  </button>
                )}
                </div>
              </div>

              <div className={`mt-4 border rounded-2xl p-4 ${isSigMobile ? 'bg-white border-slate-200 shadow-[0_10px_30px_rgba(15,23,42,0.06)]' : 'bg-gray-50 border-gray-200 rounded-lg'}`}>
                {ambientalTab === 'inspecciones' && (
                  <div className="space-y-4">
                    <div className={mobileSubLevelGroupClass}>
                      {isSigMobile && (
                        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tipo de inspeccion ambiental</div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {INSPECCIONES_AMBIENTALES_OPTIONS.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => setInspeccionesAmbientalTab(option.key)}
                            className={`px-3 ${isSigMobile ? 'py-2 min-h-[40px]' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${inspeccionesAmbientalTab === option.key ? 'text-white border-emerald-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-emerald-700'}`}
                            style={inspeccionesAmbientalTab === option.key ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <InspeccionesAmbientalModule location={location} inspectionType={inspeccionesAmbientalTab} />
                  </div>
                )}
                {ambientalTab === 'base_podas' && (
                  <div className="space-y-4">
                    <div className={mobileSubLevelGroupClass}>
                      {isSigMobile && (
                        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Operador</div>
                      )}
                      <div className={`${isSigMobile ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}`}>
                        <button
                          type="button"
                          onClick={() => setBasePodasTab('ceo')}
                          className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${basePodasTab === 'ceo' ? 'text-white border-lime-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-lime-700'}`}
                          style={basePodasTab === 'ceo' ? { background: 'linear-gradient(90deg, #65A30D 0%, #16A34A 100%)' } : undefined}
                        >
                          CEO
                        </button>
                        <button
                          type="button"
                          onClick={() => setBasePodasTab('emcali')}
                          className={`px-3 ${isSigMobile ? 'py-2.5 justify-center' : 'py-1.5'} text-xs rounded-full border inline-flex items-center gap-1 shadow-sm active:opacity-95 ${basePodasTab === 'emcali' ? 'text-white border-lime-700' : 'bg-white hover:bg-gray-50 border-gray-300 text-lime-700'}`}
                          style={basePodasTab === 'emcali' ? { background: 'linear-gradient(90deg, #65A30D 0%, #16A34A 100%)' } : undefined}
                        >
                          EMCALI
                        </button>
                      </div>
                    </div>
                    <BasePodasAmbientalModule location={location} podaType={basePodasTab} />
                  </div>
                )}
                {ambientalTab === 'gestion_residuos' && (
                  <GestionResiduosAmbientalModule location={location} />
                )}
                {ambientalTab === 'control_servicios' && (
                  <ControlServiciosAmbientalModule location={location} />
                )}
                {ambientalTab === 'especies' && (
                  <EspeciesAmbientalModule location={location} />
                )}
                {!isSigMobile && ambientalTab === 'indicadores' && (
                  <IndicadoresAmbientalesModule location={location} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isSigMobile && (
        <div className="mt-6 pb-4">
          <div className="rounded-[24px] border border-sky-200 bg-gradient-to-r from-cyan-50 via-white to-blue-50 shadow-[0_16px_36px_rgba(37,99,235,0.12)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Sincronización de datos</div>
                <div className="text-xs text-slate-500 mt-1">
                  {isOfflineMode
                    ? 'Puedes diligenciar formularios sin internet y sincronizarlos cuando vuelvas a conectarte.'
                    : 'Envía los registros pendientes y actualiza la información local de la APK.'}
                </div>
              </div>
              <div className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${pendingSyncCount > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {pendingSyncCount} pendiente{pendingSyncCount === 1 ? '' : 's'}
              </div>
            </div>
            {autoSyncNote ? (
              <div className="mt-2 text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                {autoSyncNote}
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleSyncNow}
              disabled={isSyncing}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm disabled:opacity-70"
              style={{ background: 'linear-gradient(90deg, #EA580C 0%, #F97316 52%, #FB923C 100%)' }}
            >
              <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
              {isSyncing ? 'Sincronizando...' : `Sincronizar${pendingSyncCount > 0 ? ` (${pendingSyncCount})` : ''}`}
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isDotacionesOpen}
        onClose={() => setIsDotacionesOpen(false)}
        title="Imprimir dotaciones por responsable"
        size="max-w-5xl"
      >
        <ResponsibleQueryModule
          assignments={assignments}
          materials={materialsSource}
          people={peopleSource}
          warehouses={warehouses}
          dotPrefixOnly={true}
        />
      </Modal>

      {/* Modal Solicitud Pedido */}
      <Modal isOpen={isPedidoOpen} onClose={() => setIsPedidoOpen(false)} title="Nueva Solicitud de Pedido" size="max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Solicitante (cédula o nombre) *</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Usuario actual"
                  value={(form.requesterName && form.requesterId) ? `${form.requesterName} (${form.requesterId})` : (form.requesterName || form.requesterId)}
                  readOnly
                  className="w-full border rounded px-3 py-2 pr-8"
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {}}
                  aria-label="Mostrar sugerencias"
                >
                  <Search size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{form.requesterName ? `Nombre: ${form.requesterName}` : 'Solicitante asignado al usuario actual.'}</p>

            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Colaborador (opcional)</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar por cédula o nombre..."
                  value={form.collaboratorId}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const p = findPersonByInput(raw);
                    setForm((f) => ({ ...f, collaboratorId: raw, collaboratorName: p ? p.name : '' }));
                    setShowCollaboratorSug(true);
                  }}
                  onFocus={() => setShowCollaboratorSug(true)}
                  onBlur={() => setTimeout(() => setShowCollaboratorSug(false), 120)}
                  className="w-full border rounded px-3 py-2 pr-8"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowCollaboratorSug(true)}
                  aria-label="Mostrar sugerencias"
                >
                  <Search size={16} />
                </button>
                {showCollaboratorSug && collaboratorSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border rounded shadow z-50 max-h-56 overflow-auto">
                    {collaboratorSuggestions.map((p) => (
                      <button
                        type="button"
                        key={`sug-col-${p.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setForm((f) => ({ ...f, collaboratorId: p.id, collaboratorName: p.name }));
                          setShowCollaboratorSug(false);
                        }}
                      >
                        <span className="font-medium">{p.id}</span>
                        <span className="text-gray-600"> — {p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">{form.collaboratorName ? `Nombre: ${form.collaboratorName}` : 'Quien recibe (opcional)'}</p>

            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Proyecto/Área</label>
              <select
                value={form.project}
                onChange={(e) => setForm({ ...form, project: e.target.value })}
                className="w-full border rounded px-3 py-2 bg-white"
              >
                <option value="">Seleccione...</option>
                <option value="Transversal">Transversal</option>
                <option value="Desarrollo">Desarrollo</option>
                <option value="Linea Viva">Linea Viva</option>
                <option value="Mantenimiento">Mantenimiento</option>
                <option value="EMCALI">EMCALI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha *</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-800">Ítems</h4>
              <div className="flex items-center gap-2">
                <button type="button" onClick={addItem} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700">
                  <Plus size={16} /> Agregar ítem
                </button>
                <button type="button" onClick={() => setShowStockPanel((v) => !v)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700">
                  <Search size={16} /> Agregar desde stock
                </button>
              </div>
            </div>

            {showStockPanel && (
              <div className="mb-2 border rounded p-2 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <Search size={16} />
                <input
                  type="text"
                  value={stockQuery}
                  onChange={(e) => setStockQuery(e.target.value)}
                  placeholder="Buscar material por código, nombre o marca"
                  className="flex-1 border rounded px-2 py-1"
                />
                <select
                  value={stockWarehouseId}
                  onChange={(e) => setStockWarehouseId(e.target.value)}
                  className="text-xs border rounded px-2 py-1 bg-white"
                >
                  {warehouseOptions.map((w) => (
                    <option key={`wh-${w.id}`} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="text-xs border rounded px-2 py-1 bg-white"
                >
                  {categoryOptions.map((c) => (
                    <option key={`cat-${c}`} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <ul className="divide-y max-h-40 overflow-auto">
                {stockSuggestions.map((m) => (
                  <li key={m.id} className="py-1 px-2 hover:bg-white flex items-center justify-between">
                    <div className="text-sm">
                      <span className="font-medium">{m.name}</span> <span className="text-gray-500">({m.code})</span>
                      {m.brand && <span className="text-gray-600"> - {m.brand}</span>}
                      <span className="text-xs text-gray-500 ml-2">Disp.: {stockWarehouseId === 'todas' ? Object.values(m.stockByWarehouse || {}).reduce((acc, v) => acc + (parseInt(v) || 0), 0) : (m.stockByWarehouse?.[stockWarehouseId] || 0)}</span>
                    </div>
                    <button type="button" onClick={() => addItemFromMaterial(m)} className="text-blue-600 hover:text-blue-700 text-xs">Agregar</button>
                  </li>
                ))}
                  {stockSuggestions.length === 0 && (
                    <li className="py-1 px-2 text-sm text-gray-600">No hay materiales disponibles con stock en esta sede.</li>
                  )}
                </ul>
              </div>
            )}
            <div className="overflow-x-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Descripción *</th>
                    <th className="px-3 py-2 text-left w-28">Cantidad *</th>
                    <th className="px-3 py-2 text-left w-40">Unidad</th>
                    <th className="px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={it.description}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({
                              ...f,
                              items: f.items.map((x, i) => (i === idx ? { ...x, description: v } : x)),
                            }));
                          }}
                          className="w-full border rounded px-2 py-1"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={1}
                          value={it.quantity}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setForm((f) => ({
                              ...f,
                              items: f.items.map((x, i) => (i === idx ? { ...x, quantity: v } : x)),
                            }));
                          }}
                          className="w-full border rounded px-2 py-1"
                          required
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={it.unit}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({
                              ...f,
                              items: f.items.map((x, i) => (i === idx ? { ...x, unit: v } : x)),
                            }));
                          }}
                          className="w-full border rounded px-2 py-1 bg-white"
                        >
                          <option>Unidad</option>
                          <option>Caja</option>
                          <option>Paquete</option>
                          <option>Litro</option>
                          <option>Kilogramo</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                          disabled={form.items.length === 1}
                        >
                          <Trash size={16} />
                          Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Observaciones</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full border rounded px-3 py-2 min-h-[80px]"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => setIsPedidoOpen(false)} className="px-4 py-2 rounded bg-gray-200 text-gray-800">Cancelar</button>
            <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">Guardar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
